/**
 * The drum kits a percussion part can be written on.
 *
 * The staff places below are the ones drummers have agreed on: the snare in the third
 * space, the kick in the first, the toms between them in order, the cymbals on the top
 * line and above it. They are written as pitches on a treble staff, which is not a choice
 * -- MusicXML's `display-step` and `display-octave` are read that way whatever the clef
 * over them, so C5 is the third space here exactly as it is in every other program's
 * files. Writing places rather than line numbers also means everything that draws or edits
 * a staff -- spacing, beaming, the arrow keys, the clipboard -- works on a drum part
 * unchanged, and only what sounds a note has to know the part is percussion at all.
 *
 * The keys are General MIDI's percussion map, which every synthesiser and every other
 * notation program shares, so a kit written here plays and opens as itself elsewhere.
 *
 * This is a table of common practice, not a rule: a kit is part of the document, so a
 * score can carry any kit at all and a file written somewhere else brings its own.
 */

import type { DrumNote, NoteHead, PercussionKit } from "../document/percussion";
import type { Step } from "../document/pitch";
import type { InstrumentPreset } from "./instruments";
import { AT_PITCH } from "../document/pitch";

const PERCUSSION_CLEF = { sign: "percussion" as const, line: 3, octaveChange: 0 };

function drum(
  name: string,
  midi: number,
  place: string,
  notehead: NoteHead = "normal",
  stem: DrumNote["stem"] = "auto",
  open = false,
): DrumNote {
  return {
    name,
    midi,
    step: place[0] as Step,
    octave: Number(place.slice(1)),
    notehead,
    open,
    stem,
  };
}

/**
 * A drum kit, as one player sits at it.
 *
 * Hands take stems up and feet stems down, which is how a reader tells a kick on the
 * beat from a crash on it without counting the heads.
 *
 * Where two drums share a line the commoner one is listed first, because a letter typed on
 * the staff takes the first drum written on the place it lands: the snare rather than the
 * side stick, the ride rather than its bell, the closed hi-hat rather than the open one.
 */
export const DRUM_KIT: PercussionKit = {
  name: "Drum kit",
  notes: [
    drum("Bass drum", 36, "F4", "normal", "down"),
    drum("Pedal hi-hat", 44, "D4", "x", "down"),
    drum("Low floor tom", 41, "G4", "normal", "up"),
    drum("High floor tom", 43, "A4", "normal", "up"),
    drum("Low tom", 45, "B4", "normal", "up"),
    drum("Snare drum", 38, "C5", "normal", "up"),
    drum("Side stick", 37, "C5", "x", "up"),
    drum("Low-mid tom", 47, "D5", "normal", "up"),
    drum("High tom", 50, "E5", "normal", "up"),
    drum("Ride cymbal", 51, "F5", "x", "up"),
    drum("Ride bell", 53, "F5", "diamond", "up"),
    drum("Closed hi-hat", 42, "G5", "x", "up"),
    drum("Open hi-hat", 46, "G5", "x", "up", true),
    drum("Crash cymbal", 49, "A5", "x", "up"),
  ],
};

/** One drum on a five-line staff, which is how an orchestral part is written. */
function single(name: string, midi: number, notehead: NoteHead = "normal"): PercussionKit {
  return { name, notes: [drum(name, midi, "C5", notehead)] };
}

/**
 * One instrument on a single line, which is how a part with nothing to choose is written.
 *
 * E4 because a staff's places are counted from its bottom line up whatever it has for
 * lines, so the one line of a one-line staff is the place the bottom line would be.
 */
function online(name: string, midi: number, notehead: NoteHead = "normal"): PercussionKit {
  return { name, notes: [drum(name, midi, "E4", notehead)] };
}

export const SNARE_DRUM = single("Snare drum", 38);
export const BASS_DRUM = single("Bass drum", 36);
export const TAMBOURINE = online("Tambourine", 54);
export const TRIANGLE = online("Triangle", 81);
export const WOODBLOCK = online("Woodblock", 76);

/** The kits a picker can offer, in the order it should list them. */
export const KITS: readonly PercussionKit[] = [
  DRUM_KIT,
  SNARE_DRUM,
  BASS_DRUM,
  TAMBOURINE,
  TRIANGLE,
  WOODBLOCK,
];

function percussionPart(
  id: string,
  name: string,
  abbreviation: string,
  kit: PercussionKit,
  lines: number,
): InstrumentPreset {
  return {
    id,
    name,
    abbreviation,
    family: "percussion",
    clefs: [PERCUSSION_CLEF],
    transposition: AT_PITCH,
    sound: `drum.${id}`,
    // A percussion part sounds its drums, not a program: every key it plays is its own
    // instrument on General MIDI's channel 10.
    midiProgram: 0,
    range: { low: 35, high: 81 },
    percussion: kit,
    lines,
  };
}

/** The parts that are written as drums rather than as pitches. */
export const UNPITCHED_PERCUSSION: readonly InstrumentPreset[] = [
  percussionPart("drum-kit", "Drum kit", "D. Kit", DRUM_KIT, 5),
  percussionPart("snare-drum", "Snare drum", "S.D.", SNARE_DRUM, 5),
  percussionPart("bass-drum", "Bass drum", "B.D.", BASS_DRUM, 5),
  percussionPart("tambourine", "Tambourine", "Tamb.", TAMBOURINE, 1),
  percussionPart("triangle", "Triangle", "Tri.", TRIANGLE, 1),
  percussionPart("woodblock", "Woodblock", "W.B.", WOODBLOCK, 1),
];
